import { Icons } from '@/components/icons';
import type { ISubMenu } from '@/components/sidebar/types';
import { ICON } from '@/components/sidebar/constants';
import { isFeatureEnabled } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { Urls } from '@/lib/urls';
import {
  ApartmentQueueConfig,
  ApartmentTermsConfig,
  DimensionConfigurationConfig,
  IndustrialPremisesQueueConfig,
  ParkingSpotQueueConfig,
  QueueRulesConfig,
  RoundingSettingCategoryConfig,
  type ModelName,
} from '@pigello/pigello-matrix';
import {
  Archive,
  Banknote,
  Bell,
  BookOpen,
  Box,
  BriefcaseBusiness,
  Building,
  Building2,
  CalendarDays,
  Check,
  CheckCircle,
  ClipboardCheck,
  CreditCard,
  Earth,
  FileCheck,
  FileIcon,
  FileStack,
  FileUp,
  Grid2X2,
  Grid2X2Plus,
  Info,
  KeyRound,
  LayoutTemplate,
  LifeBuoy,
  List,
  LockOpen,
  Mail,
  MonitorSmartphone,
  Paintbrush,
  Puzzle,
  ReceiptText,
  SendHorizonal,
  Settings,
  ShieldCheck,
  ShieldMinus,
  ShoppingBag,
  SlidersHorizontal,
  SquareParking,
  Tag,
  TextCursorInput,
  User,
  UserLock,
  Users,
  Wrench,
} from 'lucide-react';

type GroupType =
  | 'admin'
  | 'globalconfiguration'
  | 'security'
  | 'modulesettings';

interface IMenuGroup {
  title: string;
  isIndex?: boolean;
  key: string;
  subMenus: ISubMenu[];
}

export type SettingsMenu = Record<GroupType, IMenuGroup>;

function getSubItemsModelNames<T extends Array<ISubMenu>>(
  subItems: T,
  // Only pick out modelname from objects where the field is of type ModelName,
  // then we index into those to get a union type of all model names used
  skipModels?: Array<
    | Extract<T[number], { modelNames: ModelName[] }>['modelNames'][number]
    | Extract<T[number], { modelName: ModelName }>['modelName']
  >
): ModelName[] {
  const summedModelNames: ModelName[] = [];

  const checkModelName = (modelName: ModelName | undefined) => {
    if (!modelName) return;

    if (skipModels && skipModels.includes(modelName)) return;

    summedModelNames.push(modelName);
  };

  for (const item of subItems) {
    checkModelName(item.modelName);

    if (item.modelNames)
      for (const modelName of item.modelNames) {
        checkModelName(modelName);
      }
  }

  return summedModelNames;
}

export const contractCancellationsRouteData: ISubMenu[] = [
  {
    title: 'Inställningar',
    key: 'settings',
    path: Urls.settings.modules.base.contractcancellations.settings,
    icon: <Settings className={ICON} />,
  },
  {
    title: 'Anledningar',
    key: 'reasons',
    path: Urls.settings.modules.base.contractcancellations.reasons,
    icon: <List className={ICON} />,
  },
];

export const accountingRouteData: ISubMenu[] = [
  {
    title: 'Generella inställningar',
    key: 'general',
    path: Urls.settings.modules.invoicing.accounting.general,
    icon: <Settings className={ICON} />,
  },
  {
    title: 'Bokföringsmetod',
    key: 'method',
    path: Urls.settings.modules.invoicing.accounting.method,
    icon: <SlidersHorizontal className={ICON} />,
  },
  {
    title: 'Kontoplan',
    key: 'accounts',
    path: Urls.settings.modules.invoicing.accounting.accounts,
    icon: <Archive className={ICON} />,
  },
  {
    title: 'Verifikat',
    key: 'verifications',
    path: Urls.settings.modules.invoicing.accounting.verifications,
    icon: <BookOpen className={ICON} />,
  },
  {
    title: 'Betalmetoder',
    key: 'payment-methods',
    path: Urls.settings.modules.invoicing.accounting.paymentmethods,
    icon: <Banknote className={ICON} />,
  },
  {
    title: 'Dimensioner',
    key: 'dimensions',
    path: Urls.settings.modules.invoicing.accounting.dimensions,
    icon: <Tag className={ICON} />,
    modelName: DimensionConfigurationConfig.modelName,
  },
];

export const articlesRouteData: ISubMenu[] = [
  {
    title: 'Hyresartiklar',
    key: 'rent',
    path: Urls.settings.modules.invoicing.articles.rent,
    icon: <FileCheck className={ICON} />,
  },
  {
    title: 'Vakansartiklar',
    key: 'vacancy',
    path: Urls.settings.modules.invoicing.articles.vacancy,
    icon: <Box className={ICON} />,
  },
  {
    title: 'Faktureringsartiklar',
    key: 'invoice',
    path: Urls.settings.modules.invoicing.articles.invoice,
    icon: <ReceiptText className={ICON} />,
  },
  {
    title: 'Förbrukningsartiklar',
    key: 'consumable',
    path: Urls.settings.modules.invoicing.articles.consumable,
    icon: <ShoppingBag className={ICON} />,
  },
  {
    title: 'Artikelöverskrivningar',
    key: 'overridden',
    path: Urls.settings.modules.invoicing.articles.overriddenArticles,
    icon: <Info className={ICON} />,
  },
  {
    title: 'Kategorier',
    key: 'categories',
    path: Urls.settings.modules.invoicing.articles.categories,
    icon: <Tag className={ICON} />,
  },
];

export const invoicingSettingsRouteData: ISubMenu[] = [
  {
    title: 'Avtalsavisering',
    key: 'contractinvoicing',
    path: Urls.settings.modules.invoicing.invoicingsettings.contractinvoicing,
    icon: <FileUp className={ICON} />,
  },
  {
    title: 'Betalningsvillkor',
    key: 'paymentterms',
    path: Urls.settings.modules.invoicing.invoicingsettings.paymentterms,
    icon: <CreditCard className={ICON} />,
  },
];

export const profileSettingsRouteData: ISubMenu[] = [
  {
    title: 'Användarinformation',
    key: 'settings',
    path: Urls.settings.profile.settings,
    icon: <User className={ICON} />,
    modelName: 'organizationuser',
  },
  {
    title: 'Segment',
    key: 'segments',
    path: '/settings/profile/segments',
    icon: <Grid2X2 className={ICON} />,
    modelName: 'segment',
  },
  {
    title: 'Mina arbetsroller',
    key: 'work-engagements',
    path: '/settings/profile/work-engagements',
    icon: <BriefcaseBusiness className={ICON} />,
    modelNames: ['workengagement', 'organizationuser'],
  },

  {
    title: 'Notifikationsinställningar',
    key: 'notification-settings',
    path: '/settings/profile/notification-settings',
    icon: <Bell className={ICON} />,
  },
  {
    title: 'Sessioner',
    key: 'sessions',
    path: '/settings/profile/sessions',
    icon: <MonitorSmartphone className={ICON} />,
  },
];

export const marketRequirementProfilesData: ISubMenu[] = [
  {
    title: 'Lägenheter',
    key: 'apartment',
    icon: <Building className={ICON} />,
    path: Urls.settings.modules.market.requirementProfiles.apartment.overview,
    modelName: 'apartmentrequirementprofile',
  },
  {
    title: 'Parkeringsplatser',
    key: 'parkingspot',
    icon: <SquareParking className={ICON} />,
    path: Urls.settings.modules.market.requirementProfiles.parkingspot.overview,
    modelName: 'parkingspotrequirementprofile',
    hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET_NEW_OBJECT_TYPES),
  },
  {
    title: 'Globala inställningar',
    key: 'global-requirement-settings',
    icon: <Settings className={ICON} />,
    path: Urls.settings.modules.market.requirementProfiles.globals.overview,
    modelName: 'apartmentresidentrequirement',
  },
];

export const rentableTermsData: ISubMenu[] = [
  {
    title: 'Lägenheter',
    key: 'apartment',
    icon: <Building className={ICON} />,
    path: Urls.settings.modules.market.terms.apartment.overview,
    modelName: 'apartmentterms',
  },
];

export const queuesSidebarData: ISubMenu[] = [
  {
    title: ApartmentQueueConfig.verboseNamePlural,
    key: 'apartment-queues',
    icon: <List className={ICON} />,
    path: Urls.settings.modules.market.queues.apartmentQueues,
    modelName: 'apartmentqueue',
  },
  {
    title: IndustrialPremisesQueueConfig.verboseNamePlural,
    key: 'industrialpremises-queues',
    icon: <List className={ICON} />,
    path: Urls.settings.modules.market.queues.industrialPremisesQueues,
    modelName: 'industrialpremisesqueue',
    hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET_NEW_OBJECT_TYPES),
  },
  {
    title: ParkingSpotQueueConfig.verboseNamePlural,
    key: 'parkingspot-queues',
    icon: <List className={ICON} />,
    path: Urls.settings.modules.market.queues.parkingSpotQueues,
    modelName: 'parkingspotqueue',
    hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET_NEW_OBJECT_TYPES),
  },
  {
    title: QueueRulesConfig.verboseNamePlural,
    key: 'queue-rules',
    icon: <Settings className={ICON} />,
    path: Urls.settings.modules.market.queues.queueRules,
    modelName: 'queuerules',
  },
];

export const adminSettingsRouteData: ISubMenu[] = [
  {
    title: 'Kunder',
    key: 'customers',
    path: Urls.settings.admin.customers,
    icon: <Building2 className={ICON} />,
  },
];

const adminSubMenuItems = [
  {
    path: Urls.settings.organization.users.default,
    key: 'users',
    modelName: 'organizationuser',
    detailModalModelName: 'organizationuser',
    title: 'Användare',
    icon: <Users className={ICON} />,
  },
  {
    path: Urls.settings.organization.supportAccounts.default,
    key: 'support-accounts',
    modelName: 'organizationuser',
    detailModalModelName: 'organizationuser',
    title: 'Supportanvändare',
    icon: <LifeBuoy className={ICON} />,
  },
  {
    title: 'Behörighetsgrupper',
    icon: <LockOpen className={ICON} />,
    path: Urls.settings.organization.roles.default,
    key: 'roles',
    modelName: 'role',
  },
  {
    title: 'Arbetsroller',
    icon: <BriefcaseBusiness className={ICON} />,
    path: Urls.settings.organization.workengagements.default,
    key: 'workengagements',
    modelName: 'workengagement',
  },
  {
    title: 'Kundhantering (Admin)',
    icon: <Earth className={ICON} />,
    path: Urls.settings.admin.customers,
    key: 'admin-customers',
    pigelloAdminOnly: true,
  },
  {
    title: 'Systemadministratörer',
    icon: <Wrench className={ICON} />,
    path: Urls.settings.organization.systemManagers.default,
    key: 'system-managers',
    systemManagerOnly: true,
  },
] as const satisfies ISubMenu[];

const globalConfigurationsSubMenuItems = [
  {
    title: 'Monitorering',
    icon: <Grid2X2Plus className={ICON} />,
    path: Urls.settings.organization.monitoringconfigurations.default,
    key: 'monitoringconfigurations',
    modelName: 'monitoringconfiguration',
  },
  {
    path: Urls.settings.organization.validationconfig.default,
    title: 'Valideringar',
    icon: <Check className={ICON} />,
    key: 'validation-config',
    modelName: 'validationconfiguration',
  },
  {
    path: Urls.settings.organization.branding.default,
    title: 'Branding',
    icon: <Paintbrush className={ICON} />,
    key: 'branding',
    systemManagerOnly: true,
  },
  {
    path: Urls.settings.organization.bankid.default,
    title: 'BankID',
    icon: <UserLock className={ICON} />,
    key: 'bankid',
    systemManagerOnly: true,
    pigelloAdminOnly: true,
  },
  {
    path: Urls.settings.organization.ssoConfiguration.default,
    title: 'SSO',
    icon: <KeyRound className={ICON} />,
    key: 'sso-configuration',
    systemManagerOnly: true,
  },
  {
    path: Urls.settings.organization.tags.default,
    title: 'Taggar',
    icon: <Tag className={ICON} />,
    key: 'tags',
    modelName: 'tag',
  },
  {
    path: Urls.settings.organization.customercolumntype.default,
    title: 'Anpassade fält',
    icon: <List className={ICON} />,
    modelName: 'customercolumntype',
    key: 'customercolumns',
  },
  {
    path: Urls.settings.organization.organizationconfig.default,
    title: 'Organisationer',
    icon: <Building2 className={ICON} />,
    key: 'organization-config',
    modelName: 'organization',
  },
  {
    title: 'Integrationer',
    icon: <Puzzle className={ICON} />,
    path: Urls.settings.organization.integrations,
    key: 'allintegrations',
    modelName: 'integrationuseragent',
  },
  {
    title: 'GDPR och rensning',
    icon: <ShieldMinus className={ICON} />,
    path: Urls.settings.organization.gdprconfig.default,
    key: 'gdpr-config',
    customFunctionality: 'gdpr_admin',
  },
] as const satisfies ISubMenu[];

const securitySubMenuItems = [
  {
    path: Urls.settings.organization.securityconfig.default,
    title: 'Inloggning',
    icon: <ShieldCheck className={ICON} />,
    key: 'security-config',
    modelName: 'securityconfiguration',
  },
] as const satisfies ISubMenu[];

const baseSubMenuItems = [
  {
    title: 'Avtalsuppsägning',
    key: 'contractcancellations',
    path: Urls.settings.modules.base.contractcancellations.settings,
    modelName: 'contractcancellationconfiguration',
  },
  {
    title: 'Svenskt prisbasbelopp',
    key: 'swedishpricebasevalue',
    path: Urls.settings.modules.base.swedishPriceBase,
    modelName: 'swedishpricebasevalue',
  },
  {
    title: 'Referensränta',
    key: 'swedishreferencerate',
    path: Urls.settings.modules.base.swedishReferenceRate,
    modelName: 'swedishreferencerate',
  },
] as const satisfies ISubMenu[];

export const chatgroupConfigurationItems = [
  {
    title: 'Inställningar',
    key: 'chatgroup-configuration',
    icon: <Settings className={ICON} />,
    path: Urls.settings.modules.tenantportal.chat.chatgroupconfiguration,
  },
] as const satisfies ISubMenu[];

const errandsSubMenuItems = [
  {
    title: 'Rum & Komponenter',
    key: 'components',
    path: Urls.settings.modules.errands.componentsettings.componenttypes,
    modelNames: ['componenttype', 'componentmodel', 'roomcategory'],
  },
  {
    title: 'Ärenden',
    key: 'errandmanagement',
    modelName: 'generalerrandtype',
    path: Urls.settings.modules.errands.general.types,
  },
  {
    title: 'Besiktningar',
    key: 'inspections-settings',
    path: Urls.settings.modules.errands.inspection.types,
    modelName: 'inspectionerrandsetting',
  },
  {
    title: 'Ronderingar',
    key: 'rounds',
    path: Urls.settings.modules.errands.rounds.settings,
    modelName: 'roundingsetting',
  },
  {
    title: 'Externa leverantörer',
    key: 'external',
    modelName: 'externalsupplier',
    path: Urls.settings.modules.errands.external.suppliers,
  },
  {
    title: 'Standarduppsättningar',
    key: 'default',
    modelNames: ['generalerranddefaultdescription', 'generalerrandaction'],
    path: Urls.settings.modules.errands.default.descriptions,
  },
] as const satisfies ISubMenu[];

const invoicingSubMenuItems = [
  {
    title: 'Bokföring',
    key: 'bookkeeping',
    path: Urls.settings.modules.invoicing.accounting.accounts,
    modelName: 'bookkeepingaccountsconfig',
  },
  {
    title: 'Avtalsavisering',
    key: 'contractinvoicing',
    path: Urls.settings.modules.invoicing.invoicingsettings.contractinvoicing,
    modelNames: ['automaticcontractinvoicingsetting', 'paymenttermssetting'],
  },
  {
    title: 'Artiklar',
    key: 'articles',
    path: Urls.settings.modules.invoicing.articles.rent,
    modelNames: ['article', 'articlecategory'],
  },
  {
    title: 'Vakansbokning',
    key: 'vacancies',
    path: Urls.settings.modules.invoicing.vacancies,
    modelNames: ['automaticvacancyinvoicingsetting'],
  },
  {
    title: 'Avi-utseende',
    key: 'pdfconfig',
    path: Urls.settings.modules.invoicing.pdfconfig,
    modelName: 'invoicerelatedpdfconfiguration',
  },
  {
    title: 'Leveransinstruktioner',
    key: 'delivery-instructions',
    path: Urls.settings.modules.invoicing.deliveryinstructions,
    modelName: 'globaldeliveryinstructionsetting',
  },
  {
    path: Urls.settings.organization.retroactivityconfig.default,
    title: 'Retroaktivitet',
    icon: <LayoutTemplate className={ICON} />,
    key: 'retroactivity-config',
    modelName: 'retroactivityconfiguration',
  },
  {
    title: 'Meddelanden och bilagor',
    key: 'messages',
    path: Urls.settings.modules.invoicing.contractinvoicingmessages,
    icon: <Mail className={ICON} />,
  },
  {
    title: 'Betalfiler',
    key: 'payment-files',
    path: Urls.settings.modules.invoicing.accounting.paymentfiles,
    icon: <FileIcon className={ICON} />,
  },
] as const satisfies ISubMenu[];

export const communicationSubMenuData = [
  {
    title: 'Notismallar',
    key: 'notification-templates',
    icon: <Bell className={ICON} />,
    path: Urls.settings.modules.communication.notificationtemplates.default,
    modelName: 'notificationcontent',
  },
  {
    title: 'Utskicksmallar',
    key: 'send-out-templates',
    icon: <Icons.sendout className={ICON} />,
    path: Urls.settings.modules.communication.sendouttemplates.default,
    modelName: 'sendout',
  },
] as const satisfies ISubMenu[];

export const presetsSubMenuData = [
  {
    title: 'Kontaktpersoner',
    key: 'sub-tenants',
    icon: <Users className={ICON} />,
    path: Urls.settings.modules.presets.subtenant.default,
    modelName: 'subtenant',
  },
] as const satisfies ISubMenu[];

export const tenantPortalSubMenuData = [
  {
    title: 'Inställningar',
    key: 'customer-communication-configuration',
    path: Urls.settings.modules.tenantportal.customercommunicationconfiguration
      .default,
    icon: <Users className={ICON} />,
    modelName: 'customercommunicationconfiguration',
  },
  {
    title: 'Hyresgästaktivering',
    key: 'automationconfiguration',
    modelName: 'automationconfiguration',
    icon: <SendHorizonal className={ICON} />,
    path: Urls.settings.modules.tenantportal.automationconfiguration.default,
  },
  {
    title: 'Chattinställningar',
    key: 'chat',
    path: Urls.settings.modules.tenantportal.chat.chatgroupconfiguration,
  },
  {
    title: 'Hyresgästinformation',
    modelNames: ['tenantinformationcollection', 'faqentry'],
    key: 'faq',
    path: Urls.settings.modules.tenantportal.tenantinformation.default,
    modelName: 'faqentry',
  },
] as const satisfies ISubMenu[];

export const marketSubMenuItems = [
  {
    title: 'Köer',
    key: 'queues',
    path: Urls.settings.modules.market.queues.apartmentQueues,
    icon: <List className={ICON} />,
    modelName: 'apartmentqueue',
  },
  {
    title: 'Uthyrningsvillkor',
    key: 'rentable-terms',
    path: Urls.settings.modules.market.terms.apartment.overview,
    icon: <Settings className={ICON} />,
    modelName: ApartmentTermsConfig.modelName,
  },
  {
    title: 'Kravprofiler',
    key: 'requirements-profiles',
    path: Urls.settings.modules.market.requirementProfiles.apartment.overview,
    icon: <Settings className={ICON} />,
    modelName: 'apartmentrequirementprofile',
  },
] as const satisfies ISubMenu[];

const moduleSubMenuItems = [
  {
    title: 'Bas',
    key: 'base',
    path: '/base',
    icon: <Box className={ICON} />,
    modelNames: getSubItemsModelNames(baseSubMenuItems),
    subMenus: baseSubMenuItems,
  },
  {
    title: 'Avisering & Fakturering',
    icon: <Banknote className={ICON} />,
    key: 'invoicing',
    modelNames: getSubItemsModelNames(invoicingSubMenuItems),
    subMenus: invoicingSubMenuItems,
  },

  {
    title: 'Teknisk Förvaltning',
    icon: <BriefcaseBusiness className={ICON} />,
    path: '/errands',
    key: 'errands-settings',
    modelNames: getSubItemsModelNames(errandsSubMenuItems),
    subMenus: errandsSubMenuItems,
  },
  {
    title: 'Kommunikation',
    key: 'communication',
    icon: <Mail className={ICON} />,
    subMenus: communicationSubMenuData,
    modelNames: getSubItemsModelNames(communicationSubMenuData),
  },
  {
    title: 'Användarmallar',
    key: 'presets',
    icon: <FileStack className={ICON} />,
    subMenus: presetsSubMenuData,
    modelNames: getSubItemsModelNames(presetsSubMenuData),
  },
  {
    title: 'Mina Sidor',
    key: 'tenant-portal',
    icon: <Users className={ICON} />,
    modelNames: getSubItemsModelNames(tenantPortalSubMenuData),
    subMenus: tenantPortalSubMenuData,
  },
  {
    title: 'Marknad & Annonsering',
    key: 'market',
    icon: <Icons.market className={ICON} />,
    modelNames: getSubItemsModelNames(marketSubMenuItems),
    subMenus: marketSubMenuItems,
    hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET),
  },
] as const satisfies ISubMenu[];

export const errandsGeneralRouteData: ISubMenu[] = [
  {
    title: 'Ärendetyper',
    key: 'types',
    path: Urls.settings.modules.errands.general.types,
    icon: <BriefcaseBusiness className={ICON} />,
  },
];
export const errandsComponentRouteData: ISubMenu[] = [
  {
    title: 'Komponenttyper',
    key: 'component-types',
    path: Urls.settings.modules.errands.componentsettings.componenttypes,
    icon: <Tag className={ICON} />,
  },
  {
    title: 'Komponentmodeller',
    key: 'component-models',
    path: Urls.settings.modules.errands.componentsettings.componentmodels,
    icon: <Box className={ICON} />,
  },
  {
    title: 'Rumskategorier',
    key: 'roomcategories',
    path: Urls.settings.modules.errands.componentsettings.roomcategories,
    icon: <Archive className={ICON} />,
  },
  {
    title: 'Komponentskick',
    key: 'component-condition-values',
    path: Urls.settings.modules.errands.componentsettings
      .componentconditionvalues,
    icon: <CheckCircle className={ICON} />,
  },
];

export const errandsExternalRouteData: ISubMenu[] = [
  {
    title: 'Leverantörer',
    key: 'suppliers',
    path: Urls.settings.modules.errands.external.suppliers,
    icon: <BriefcaseBusiness className={ICON} />,
  },
];

export const errandsDefaultRouteData: ISubMenu[] = [
  {
    title: 'Standardbeskrivningar',
    key: 'descriptions',
    path: Urls.settings.modules.errands.default.descriptions,
    icon: <BriefcaseBusiness className={ICON} />,
  },
  {
    title: 'Standardåtgärder',
    key: 'actions',
    path: Urls.settings.modules.errands.default.actions,
    icon: <Wrench className={ICON} />,
  },
];

export const errandInspectionsRouteData: ISubMenu[] = [
  {
    title: 'Besiktningstyper',
    key: 'types',
    path: Urls.settings.modules.errands.inspection.types,
    icon: <BriefcaseBusiness className={ICON} />,
  },
  {
    title: 'Protokollinställningar',
    key: 'protocolsettings',
    path: Urls.settings.modules.errands.inspection.protocolsettings,
    icon: <ClipboardCheck className={ICON} />,
  },
  {
    title: 'Egna fält',
    key: 'custom-fields',
    path: Urls.settings.modules.errands.inspection.customfields,
    icon: <TextCursorInput className={ICON} />,
  },
];

export const roundsRouteData: ISubMenu[] = [
  {
    title: 'Ronderingsscheman',
    key: 'settings',
    path: Urls.settings.modules.errands.rounds.settings,
    icon: <CalendarDays className='mr-2 size-4' />,
  },
  {
    title: 'Mallar',
    key: 'templates',
    path: Urls.settings.modules.errands.rounds.templates,
    icon: <BriefcaseBusiness className='mr-2 size-4' />,
  },
  {
    title: RoundingSettingCategoryConfig.verboseNamePlural,
    key: 'categories',
    path: Urls.settings.modules.errands.rounds.categories,
    icon: <Tag className='mr-2 size-4' />,
  },
  {
    title: 'Kontrollpunktstyper',
    key: 'checklist-row-categories',
    path: Urls.settings.modules.errands.rounds.checklistrowcategories,
    icon: <List className='mr-2 size-4' />,
  },
  {
    title: 'Egna fält',
    key: 'custom-fields',
    path: Urls.settings.modules.errands.rounds.customfields,
    icon: <TextCursorInput className='mr-2 size-4' />,
  },
];

export const settingsMenuData: SettingsMenu = {
  admin: {
    title: 'Admin',
    isIndex: true,
    key: 'admin',
    subMenus: adminSubMenuItems,
  },
  globalconfiguration: {
    title: 'Globala konfigurationer',
    key: 'globalConfiguration',
    subMenus: globalConfigurationsSubMenuItems,
  },
  security: {
    title: 'Säkerhet',
    key: 'security',
    subMenus: securitySubMenuItems,
  },
  modulesettings: {
    title: 'Modulinställningar',
    key: 'modulesettings',
    subMenus: moduleSubMenuItems,
  },
} as const satisfies SettingsMenu;
